import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { CalendarRange, Loader2, ChevronDown, ChevronUp, Send, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import PlanningGrid from './PlanningGrid';

export default function PlanningAdminPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/planning/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/planning/campaign-preview`, { withCredentials: true }),
      ]);
      setData(r1.data);
      setCampaign(r2.data);
    } catch (e) { toast.error('Impossible de charger le planning.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <Card className="border-[#E9DDCC] rounded-2xl"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></CardContent></Card>;
  }

  const k = data.kpis;

  const cancelRes = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/planning/reservations/${id}/cancel`, {}, { withCredentials: true });
      toast.success(r.data.promoted ? `Annulée — ${r.data.promoted} (liste d'attente) a récupéré la place et a été notifiée.` : 'Réservation annulée.');
      load();
    } catch (e) { toast.error('Erreur lors de l\'annulation.'); }
  };

  const updateNotif = async (key, value) => {
    const next = { ...data.notif_settings, [key]: value };
    await axios.put(`${API}/admin/planning/notif-settings`, next, { withCredentials: true });
    setData({ ...data, notif_settings: next });
    toast.success('Paramètres de notification enregistrés.');
  };

  const sendCampaign = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/planning/campaign-send`, {}, { withCredentials: true });
      toast.success(`Campagne envoyée à ${r.data.sent} cliente(s).`);
      setConfirmSend(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur d\'envoi.'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="planning-admin-panel">
      <div className="flex items-center gap-2">
        <CalendarRange className="w-4 h-4 text-[#C99A55]" />
        <h3 className="font-heading text-lg text-[#493329]">Planning & Réservations</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
        {[['Pré-réservations', k.total_prereservations, 'kpi-total'], ['Clientes uniques', k.unique_clients, 'kpi-unique'],
          ['Occupation globale', `${k.global_occupancy} %`, 'kpi-occupancy'], ['Séances complètes', k.full_slots, 'kpi-full'],
          ['Liste d\'attente', k.total_waitlist, 'kpi-waitlist']].map(([l, v, tid]) => (
          <div key={tid} className="bg-white rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">{l}</div>
            <div className="font-heading text-xl text-[#493329]" data-testid={tid}>{v}</div>
          </div>
        ))}
      </div>

      <PlanningGrid slots={data.slots} onSelect={setDetail} testPrefix="admin" />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-slot-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">
              {detail && `${detail.day} ${detail.start} – ${detail.end} — ${detail.type.replace('Pilates Reformer – ', '')}`}
            </DialogTitle>
            <DialogDescription className="sr-only">Détail du créneau : inscrites, liste d'attente et historique</DialogDescription>
          </DialogHeader>
          {detail && (() => {
            const s = data.slots.find((x) => x.id === detail.id) || detail;
            return (
              <div className="space-y-4 text-sm">
                <div className="bg-[#F5EFE5] rounded-xl p-3 text-xs text-[#7A6455]">
                  <strong className="text-[#493329]">{s.reserved} / {s.capacity}</strong> places · {s.occupancy} % d'occupation
                  {s.waitlist_count > 0 && <> · <strong className="text-[#C99A55]">{s.waitlist_count} en liste d'attente</strong></>}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-1.5">Inscrites ({s.participants.length})</p>
                  {s.participants.length === 0 && <p className="text-xs text-[#AE9885]">Aucune pré-réservation.</p>}
                  {s.participants.map((p) => (
                    <div key={p.reservation_id} className="flex items-center justify-between bg-[#F5EFE5] rounded-lg px-3 py-2 mb-1.5">
                      <span className="min-w-0 text-xs">
                        <span className="text-[#493329] text-sm">{p.name}</span>
                        {p.promoted && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#EDF2EA] text-[#5C7057] text-[10px]">Repêchée de l'attente</span>}
                        <span className="block text-[#7A6455] truncate">{p.email}{p.phone ? ` · ${p.phone}` : ''}</span>
                      </span>
                      <Button variant="outline" size="sm" onClick={() => { cancelRes(p.reservation_id); setDetail(null); }} className="h-7 text-xs rounded-full border-[#E9DDCC] text-[#C76542] shrink-0" data-testid={`detail-cancel-${p.reservation_id}`}>Annuler</Button>
                    </div>
                  ))}
                </div>
                {s.waitlist.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1">Liste d'attente ({s.waitlist.length})</p>
                    {s.waitlist.map((w) => (
                      <div key={w.reservation_id} className="text-xs text-[#7A6455] py-0.5">{w.position}. {w.name} · {w.email}</div>
                    ))}
                  </div>
                )}
                {s.history && s.history.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-1">Historique (annulations & remplacements)</p>
                    {s.history.map((h, i) => (
                      <div key={i} className="text-xs text-[#7A6455] py-0.5">
                        {h.name} — annulée{h.cancelled_at ? ` le ${new Date(h.cancelled_at).toLocaleDateString('fr-FR')}` : ''}{h.replaced_by ? ` · remplacée par ${h.replaced_by}` : ''}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#AE9885]">Le pointage présente / absente / no-show sera disponible à l'ouverture du studio.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-2xl border border-[#E9DDCC] divide-y divide-[#EFE4D0]">
        {data.slots.map((s) => (
          <div key={s.id}>
            <button onClick={() => setOpen(open === s.id ? null : s.id)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid={`admin-slot-${s.id}`}>
              <div>
                <span className="text-sm font-medium text-[#493329]">{s.day} {s.start} — {s.type.replace('Pilates Reformer – ', '')}</span>
                <span className="block text-xs text-[#7A6455]">{s.reserved} / {s.capacity} places · {s.occupancy} % {s.waitlist_count > 0 && `· ${s.waitlist_count} en attente`}</span>
              </div>
              <div className="flex items-center gap-2">
                {s.alert === 'complet' && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">Complet</span>}
                {s.alert === 'presque_complet' && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">Presque complet</span>}
                {open === s.id ? <ChevronUp className="w-4 h-4 text-[#AE9885]" /> : <ChevronDown className="w-4 h-4 text-[#AE9885]" />}
              </div>
            </button>
            {open === s.id && (
              <div className="px-4 pb-4 text-sm space-y-2">
                {s.participants.length === 0 && <p className="text-[#AE9885] text-xs">Aucune pré-réservation.</p>}
                {s.participants.map((p) => (
                  <div key={p.reservation_id} className="flex items-center justify-between bg-[#F5EFE5] rounded-lg px-3 py-2">
                    <span>{p.name} <span className="text-xs text-[#7A6455]">· {p.email}</span></span>
                    <Button variant="outline" size="sm" onClick={() => cancelRes(p.reservation_id)} className="h-7 text-xs rounded-full border-[#E9DDCC] text-[#C76542]" data-testid={`cancel-${p.reservation_id}`}>Annuler</Button>
                  </div>
                ))}
                {s.waitlist.length > 0 && (
                  <div className="pt-1">
                    <p className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1">Liste d'attente ({s.waitlist.length})</p>
                    {s.waitlist.map((w) => (
                      <div key={w.reservation_id} className="text-xs text-[#7A6455] py-0.5">{w.position}. {w.name} · {w.email}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Statistiques de demande</p>
          <div className="text-xs space-y-1.5 text-[#7A6455]">
            <div><strong className="text-[#493329]">Par jour :</strong> {Object.entries(data.stats.by_day).map(([d, v]) => `${d} ${v}%`).join(' · ')}</div>
            <div><strong className="text-[#493329]">Matin / Soir :</strong> {data.stats.by_moment.matin}% / {data.stats.by_moment.soir}%</div>
            <div><strong className="text-[#493329]">Par type :</strong> {Object.entries(data.stats.by_type).map(([t, v]) => `${t} : ${v}`).join(' · ')}</div>
            <div className="pt-1"><strong className="text-[#493329]">Top demandes :</strong>
              {data.stats.most_demanded.map((m, i) => <div key={i}>· {m.label} ({m.reserved} rés.{m.waitlist ? `, ${m.waitlist} att.` : ''})</div>)}
            </div>
            <div><strong className="text-[#493329]">Moins demandés :</strong>
              {data.stats.least_demanded.map((m, i) => <div key={i}>· {m.label} ({m.reserved} rés.)</div>)}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2 flex items-center gap-1"><Bell className="w-3 h-3" /> Notifications de réservation</p>
            <div className="flex items-center justify-between text-sm py-1">
              <span>Dashboard</span>
              <Switch checked={data.notif_settings.dashboard} onCheckedChange={(v) => updateNotif('dashboard', v)} data-testid="notif-dashboard-switch" />
            </div>
            <div className="flex items-center justify-between text-sm py-1">
              <span>Email administrateur</span>
              <Switch checked={data.notif_settings.email} onCheckedChange={(v) => updateNotif('email', v)} data-testid="notif-email-switch" />
            </div>
          </div>

          <div className="bg-[#F3EADA] rounded-2xl border border-[#C99A55]/40 p-4" data-testid="campaign-block">
            <p className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1">Campagne — « Le planning PURO est ouvert »</p>
            <p className="text-xs text-[#7A6455] mb-2">{campaign?.targets ?? 0} cliente(s) recevront cette notification. Aucun envoi sans votre validation.</p>
            {!confirmSend ? (
              <Button onClick={() => setConfirmSend(true)} className="w-full bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-10 text-sm" data-testid="campaign-send-btn">
                <Send className="w-4 h-4 mr-2" /> Envoyer la notification à toutes les clientes
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setConfirmSend(false)} disabled={sending} className="flex-1 rounded-xl h-10 text-sm border-[#E9DDCC]" data-testid="campaign-cancel-btn">Annuler</Button>
                <Button onClick={sendCampaign} disabled={sending} className="flex-1 bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-10 text-sm" data-testid="campaign-confirm-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Oui, envoyer (${campaign?.targets ?? 0})`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4" data-testid="admin-notifications-list">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Dernières notifications</p>
        {data.notifications.length === 0 ? <p className="text-xs text-[#AE9885]">Aucune notification pour le moment.</p> : (
          <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
            {data.notifications.map((n) => (
              <div key={n.id} className="text-xs text-[#7A6455] bg-[#F5EFE5] rounded-lg px-3 py-2">
                <strong className="text-[#493329]">{n.kind}</strong> — {n.message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
